import { format, subDays } from "date-fns";

export type OmnichannelTabId =
  | "overview"
  | "sales"
  | "inventory"
  | "customers"
  | "financials";

export type OmnichannelSource = "shopify" | "odoo";
export type OmnichannelChannel = "online" | "physical";
export type OmnichannelStatus =
  | "completed"
  | "pending"
  | "refunded"
  | "cancelled";

export interface OmnichannelTabItem {
  id: OmnichannelTabId;
  label: string;
}

export interface OmnichannelOrder {
  id: string;
  orderNumber: string;
  source: OmnichannelSource;
  channel: OmnichannelChannel;
  customerName: string;
  customerEmail: string;
  items: number;
  total: number;
  tax: number;
  status: OmnichannelStatus;
  date: string;
}

export interface OmnichannelProduct {
  id: string;
  name: string;
  sku: string;
  category: string;
  price: number;
  cost: number;
  shopifyStock: number;
  odooStock: number;
  totalStock: number;
  reorderPoint: number;
  status: "in_stock" | "low_stock" | "out_of_stock";
}

export interface OmnichannelCustomer {
  id: string;
  name: string;
  email: string;
  source: OmnichannelSource;
  totalOrders: number;
  totalSpent: number;
  avgOrderValue: number;
  firstOrder: string;
  lastOrder: string;
  type: "new" | "returning";
  cltv: number;
}

export interface DailySalesPoint {
  date: string;
  shopify: number;
  odoo: number;
}

export interface MonthlyPoint {
  month: string;
  revenue: number;
  expenses: number;
  profit: number;
}

export interface DistributionPoint {
  label: string;
  value: number;
}

export interface ChannelPoint extends DistributionPoint {
  channel: OmnichannelChannel;
  percentage: number;
}

const NOW = new Date("2026-04-02T00:00:00");

const seededRandom = (seed: number) => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

const productNames = [
  "Organic Cotton T-Shirt",
  "Wireless Earbuds Pro",
  "Bamboo Water Bottle",
  "Leather Laptop Sleeve",
  "Ceramic Coffee Mug Set",
  "Smart Watch Band",
  "Yoga Mat Premium",
  "LED Desk Lamp",
  "Stainless Steel Tumbler",
  "Natural Soap Bar Set",
  "Bluetooth Speaker Mini",
  "Canvas Tote Bag",
  "Essential Oil Diffuser",
  "Silicone Phone Case",
  "Wooden Cutting Board",
  "Recycled Notebook Set",
  "USB-C Hub Adapter",
  "Linen Throw Pillow",
  "Glass Food Containers",
  "Aromatherapy Candle",
];

const categories = [
  "Apparel",
  "Electronics",
  "Home & Kitchen",
  "Accessories",
  "Wellness",
];

const customerNames = [
  "Emma Wilson",
  "James Chen",
  "Sofia Rodriguez",
  "Liam O'Brien",
  "Ava Patel",
  "Noah Kim",
  "Isabella Santos",
  "Oliver Martinez",
  "Mia Thompson",
  "Ethan Davis",
  "Charlotte Brown",
  "Lucas Garcia",
  "Amelia Lee",
  "Mason Taylor",
  "Harper Anderson",
  "Aiden Moore",
  "Evelyn White",
  "Elijah Harris",
  "Abigail Clark",
  "Logan Lewis",
  "Emily Robinson",
  "Alexander Walker",
  "Scarlett Hall",
  "Sebastian Young",
  "Victoria King",
];

export const OMNICHANNEL_TAB_ITEMS: OmnichannelTabItem[] = [
  { id: "overview", label: "Overview" },
  { id: "sales", label: "Sales" },
  { id: "inventory", label: "Inventory" },
  { id: "customers", label: "Customers" },
  { id: "financials", label: "Financials" },
];

export const generateOmnichannelProducts = (): OmnichannelProduct[] =>
  productNames.map((name, index) => {
    const seed = index * 7 + 3;
    const price = Math.round((15 + seededRandom(seed) * 85) * 100) / 100;
    const cost = Math.round(
      price * (0.3 + seededRandom(seed + 1) * 0.3) * 100
    ) / 100;
    const shopifyStock = Math.floor(seededRandom(seed + 2) * 200);
    const odooStock = Math.floor(seededRandom(seed + 3) * 150);
    const totalStock = shopifyStock + odooStock;
    const reorderPoint = Math.floor(15 + seededRandom(seed + 4) * 35);

    return {
      id: `prod-${index + 1}`,
      name,
      sku: `SKU-${String(1000 + index)}`,
      category: categories[index % categories.length],
      price,
      cost,
      shopifyStock,
      odooStock,
      totalStock,
      reorderPoint,
      status:
        totalStock === 0
          ? "out_of_stock"
          : totalStock < reorderPoint
            ? "low_stock"
            : "in_stock",
    };
  });

export const generateOmnichannelOrders = (): OmnichannelOrder[] => {
  const orders: OmnichannelOrder[] = [];
  const sources: OmnichannelSource[] = ["shopify", "odoo"];
  const channels: OmnichannelChannel[] = ["online", "physical"];
  const statuses: OmnichannelStatus[] = [
    "completed",
    "completed",
    "completed",
    "completed",
    "pending",
    "refunded",
    "cancelled",
  ];

  for (let index = 0; index < 320; index += 1) {
    const seed = index * 13 + 7;
    const date = format(
      subDays(NOW, Math.floor(seededRandom(seed) * 365)),
      "yyyy-MM-dd"
    );
    const source = sources[Math.floor(seededRandom(seed + 1) * sources.length)];
    const channel =
      source === "odoo" && seededRandom(seed + 5) > 0.45
        ? "physical"
        : channels[Math.floor(seededRandom(seed + 2) * channels.length)];
    const total = Math.round((25 + seededRandom(seed + 3) * 520) * 100) / 100;
    const customerIdx = Math.floor(seededRandom(seed + 4) * customerNames.length);

    orders.push({
      id: `ord-${String(index + 1).padStart(4, "0")}`,
      orderNumber: `#${10000 + index}`,
      source,
      channel,
      customerName: customerNames[customerIdx],
      customerEmail: `${customerNames[customerIdx]
        .toLowerCase()
        .replace(/[' ]/g, ".")}@example.com`,
      items: 1 + Math.floor(seededRandom(seed + 6) * 5),
      total,
      tax: Math.round(total * 0.08 * 100) / 100,
      status: statuses[Math.floor(seededRandom(seed + 7) * statuses.length)],
      date,
    });
  }

  return orders.sort((a, b) => b.date.localeCompare(a.date));
};

export const generateOmnichannelCustomers = (): OmnichannelCustomer[] =>
  customerNames.map((name, index) => {
    const seed = index * 17 + 11;
    const totalOrders = 1 + Math.floor(seededRandom(seed) * 30);
    const avgOrderValue = Math.round(
      (30 + seededRandom(seed + 1) * 170) * 100
    ) / 100;
    const totalSpent = Math.round(totalOrders * avgOrderValue * 100) / 100;

    return {
      id: `cust-${index + 1}`,
      name,
      email: `${name.toLowerCase().replace(/[' ]/g, ".")}@example.com`,
      source: seededRandom(seed + 4) > 0.5 ? "shopify" : "odoo",
      totalOrders,
      totalSpent,
      avgOrderValue,
      firstOrder: format(
        subDays(NOW, 30 + Math.floor(seededRandom(seed + 2) * 335)),
        "yyyy-MM-dd"
      ),
      lastOrder: format(
        subDays(NOW, Math.floor(seededRandom(seed + 3) * 30)),
        "yyyy-MM-dd"
      ),
      type: totalOrders > 3 ? "returning" : "new",
      cltv: Math.round(
        totalSpent * (1 + seededRandom(seed + 5) * 0.5) * 100
      ) / 100,
    };
  });

export const getOverviewSalesTrend = (days = 60): DailySalesPoint[] => {
  const data: DailySalesPoint[] = [];

  for (let index = days; index >= 0; index -= 1) {
    const seed = index * 23 + 5;
    const day = subDays(NOW, index).getDay();
    const weekendFactor = day === 0 || day === 6 ? 1.3 : 1;
    const trendFactor = 1 + (days - index) * 0.003;

    const shopify = Math.round(
      (800 + seededRandom(seed) * 2200) * weekendFactor * trendFactor
    );
    const odoo = Math.round(
      (500 + seededRandom(seed + 1) * 1500) * weekendFactor * trendFactor
    );

    data.push({
      date: format(subDays(NOW, index), "MMM dd"),
      shopify,
      odoo,
    });
  }

  return data;
};

export const getChannelDistribution = (
  orders: OmnichannelOrder[]
): ChannelPoint[] => {
  const map: Record<OmnichannelChannel, { value: number; label: string }> = {
    online: { value: 0, label: "Online Store" },
    physical: { value: 0, label: "Physical Store" },
  };

  orders
    .filter(order => order.status === "completed")
    .forEach(order => {
      map[order.channel].value += order.total;
    });

  const total = Object.values(map).reduce((sum, item) => sum + item.value, 0);

  return Object.entries(map).map(([channel, item]) => ({
    channel: channel as OmnichannelChannel,
    label: item.label,
    value: Math.round(item.value),
    percentage: total > 0 ? Math.round((item.value / total) * 100) : 0,
  }));
};

export const getFinancialSummary = (orders: OmnichannelOrder[]) => {
  const completed = orders.filter(order => order.status === "completed");
  const refunded = orders.filter(order => order.status === "refunded");

  const revenue = completed.reduce((sum, order) => sum + order.total, 0);
  const taxes = completed.reduce((sum, order) => sum + order.tax, 0);
  const refunds = refunded.reduce((sum, order) => sum + order.total, 0);
  const cogs = revenue * 0.42;
  const expenses = revenue * 0.18;

  return {
    revenue: Math.round(revenue),
    cogs: Math.round(cogs),
    grossProfit: Math.round(revenue - cogs),
    taxes: Math.round(taxes),
    expenses: Math.round(expenses),
    netProfit: Math.round(revenue - cogs - taxes - expenses - refunds),
    refunds: Math.round(refunds),
  };
};

export const getOverviewKpis = (
  orders: OmnichannelOrder[],
  customers: OmnichannelCustomer[],
  products: OmnichannelProduct[]
) => {
  const completed = orders.filter(order => order.status === "completed");
  const revenue = completed.reduce((sum, order) => sum + order.total, 0);
  const avgCltv =
    customers.reduce((sum, customer) => sum + customer.cltv, 0) / customers.length;
  const inventoryValue = products.reduce(
    (sum, product) => sum + product.totalStock * product.cost,
    0
  );

  return {
    totalRevenue: Math.round(revenue),
    totalOrders: completed.length,
    activeCustomers: customers.length,
    avgOrderValue: Math.round(revenue / Math.max(completed.length, 1)),
    inventoryValue: Math.round(inventoryValue),
    avgCltv: Math.round(avgCltv),
  };
};

export const getMonthlyFinancials = (
  orders: OmnichannelOrder[]
): MonthlyPoint[] => {
  const months: Record<string, MonthlyPoint> = {};

  for (let index = 11; index >= 0; index -= 1) {
    const month = format(subDays(NOW, index * 30), "MMM yyyy");
    months[month] = { month, revenue: 0, expenses: 0, profit: 0 };
  }

  orders
    .filter(order => order.status === "completed")
    .forEach(order => {
      const month = format(new Date(order.date), "MMM yyyy");
      if (!months[month]) {
        return;
      }
      months[month].revenue += order.total;
      months[month].expenses += order.total * 0.6;
      months[month].profit += order.total * 0.4;
    });

  return Object.values(months).map(item => ({
    ...item,
    revenue: Math.round(item.revenue),
    expenses: Math.round(item.expenses),
    profit: Math.round(item.profit),
  }));
};

export const getCltvBuckets = (
  customers: OmnichannelCustomer[]
): DistributionPoint[] => [
  { label: "PKR 0-200", value: customers.filter(customer => customer.cltv < 200).length },
  {
    label: "PKR 200-500",
    value: customers.filter(
      customer => customer.cltv >= 200 && customer.cltv < 500
    ).length,
  },
  {
    label: "PKR 500-1k",
    value: customers.filter(
      customer => customer.cltv >= 500 && customer.cltv < 1000
    ).length,
  },
  {
    label: "PKR 1k-2k",
    value: customers.filter(
      customer => customer.cltv >= 1000 && customer.cltv < 2000
    ).length,
  },
  { label: "PKR 2k+", value: customers.filter(customer => customer.cltv >= 2000).length },
];

export const getCustomerSegmentation = (
  customers: OmnichannelCustomer[]
): DistributionPoint[] => [
  {
    label: "New",
    value: customers.filter(customer => customer.type === "new").length,
  },
  {
    label: "Returning",
    value: customers.filter(customer => customer.type === "returning").length,
  },
];

export const getTaxBreakdown = (taxes: number): DistributionPoint[] => [
  { label: "Sales Tax", value: Math.round(taxes * 0.65) },
  { label: "VAT", value: Math.round(taxes * 0.25) },
  { label: "Other", value: Math.round(taxes * 0.1) },
];

export const paginateRows = <T,>(rows: T[], page: number, pageSize = 10) =>
  rows.slice((page - 1) * pageSize, page * pageSize);
